import React, { useContext, useEffect, useState } from 'react';
import classnames from 'classnames/bind';

import { CartService } from '@services/cart';

import styles from './CartDrawer.module.scss';
import Drawer from '@mui/material/Drawer';
import { useServiceRef } from 'hooks';
import { CartContext, pushBeginCheckoutEvent, pushViewCartEvent } from '@utils';
import { Button, Cart } from '@components';
import { ROUTES } from '@constants';
import { useRouter } from 'next/router';
import { DiscountCodeReference } from '@commercetools/platform-sdk';
import { Cart as CartInterface, CartOutOfStockItems } from '@services/cart/interfaces';

const CN = 'cart-drawer';
const buttonClassName = `${CN}--button`;
const cx = classnames.bind(styles);

interface Props {
  isDrawerOpen: boolean,
  onClose: () => void,
}

export const CartDrawer: React.FC<Props> = ({
  isDrawerOpen,
  onClose
}) => {
  const { push } = useRouter();
  const { cart, setCart } = useContext(CartContext);
  const [ outOfStockItems, setOutOfStockItems ] = useState<CartOutOfStockItems[]>([]);
  const [ isLoadingDiscount, setIsLoadingDiscount ] = useState(false);
  const [ isLoadingLineItems, setIsLoadingLineItems ] = useState(false);
  const cartServiceRef = useServiceRef(CartService);

  const setupOutOfStockLines = async (cart: CartInterface | null): Promise<void> => {
    if (!cart || !cart.lineItems.length) {
      return;
    }

    const { unavailables, version } = await cartServiceRef.current.checkOutOfStockCart(cart.id, cart.version);
    if (version > cart.version) {
      setCart({ ...cart, version });
    }

    setOutOfStockItems(unavailables);
  };

  const handleItemQuantityChange = async (lineItemId: string, quantity: number): Promise<void> => {
    if (!cart) return;

    setIsLoadingLineItems(true);

    const newCart = await cartServiceRef.current.changeLineItemQuantity(
      cart.id,
      cart.version,
      lineItemId,
      quantity
    );

    if (newCart) {
      await setupOutOfStockLines(newCart);
    }

    setIsLoadingLineItems(false);
  };

  const handleLineItemRemove = async (lineItemId: string): Promise<void> => {
    if (!cart) return;

    setIsLoadingLineItems(true);

    const newCart = await cartServiceRef.current.removeLineItem(
      cart?.id,
      cart?.version,
      lineItemId
    );
    if (newCart) {
      setCart(newCart);
    }

    setIsLoadingLineItems(false);
  };

  const handleAddDiscountCode = async (discountCode: string): Promise<void> => {
    if (cart) {
      setIsLoadingDiscount(true);
      const newCart = await cartServiceRef.current.addDiscountCode(
        cart?.id,
        cart?.version,
        discountCode
      );
      setIsLoadingDiscount(false);

      if (newCart) {
        setCart(newCart);
      }
    }
  };

  const handleRemoveDiscountCode = async (discountCode: DiscountCodeReference): Promise<void> => {
    if (cart) {
      setIsLoadingDiscount(true);
      const newCart = await cartServiceRef.current.removeDiscountCode(
        cart?.id,
        cart?.version,
        discountCode
      );
      setIsLoadingDiscount(false);

      if (newCart) {
        setCart(newCart);
      }
    }
  };

  const handleClickOnCheckoutButton = (): void => {
    pushBeginCheckoutEvent(cart);
    onClose();
    push(ROUTES.checkout);
  };

  useEffect(() => {
    if (isDrawerOpen && cart) {
      setupOutOfStockLines(cart);
      pushViewCartEvent(cart);
    }
  }, [ isDrawerOpen ]);

  return (
    <Drawer
      aria-label='cart-drawer'
      anchor='right'
      open={ isDrawerOpen }
      onClose={ onClose }
    >
      <div className={ cx(CN) }>
        <Cart
          cart={ cart }
          onAddDiscountCode={ handleAddDiscountCode }
          onRemoveDiscountCode={ handleRemoveDiscountCode }
          onItemQuantityChange= { handleItemQuantityChange }
          onLineItemRemove={ handleLineItemRemove }
          isLoadingDiscount={ isLoadingDiscount }
          isLoadingLineItems={ isLoadingLineItems }
          onClose={ onClose }
          outOfStockItems={ outOfStockItems }
        />
        {cart?.lineItems.length ? (<Button
          className={ cx(buttonClassName) }
          onClick={ handleClickOnCheckoutButton }
        >
          Checkout
        </Button>) : null}
      </div>
    </Drawer>
  );
};
